import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ecommerceApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState } from '../../components/common';
import { getErrorMessage } from '../../api/client';

import { Modal } from '../../components/Modal';

/** FR-ECOM-08 — Email campaigns */
export function CampaignsPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const q = useQuery({
    queryKey: ['campaigns', statusFilter],
    queryFn: () => ecommerceApi.listCampaigns({ status: statusFilter || undefined, limit: 100 }),
  });

  const kpis = (() => {
    const rows = q.data ?? [];
    let total = rows.length, scheduled = 0, sent = 0, totalRecipients = 0;
    for (const c of rows) {
      if (c.status === 'scheduled') scheduled++;
      if (c.status === 'sent') sent++;
      totalRecipients += c.recipient_count || 0;
    }
    return { total, scheduled, sent, totalRecipients };
  })();

  const STATUS_OPTIONS = [
    { value: '',          label: 'All' },
    { value: 'draft',     label: 'Draft' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'sending',   label: 'Sending' },
    { value: 'sent',      label: 'Sent' },
    { value: 'failed',    label: 'Failed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Online store"
        title="Email campaigns"
        subtitle="Create, schedule, and measure email campaigns to your customer base."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New campaign
          </button>
        }
      />

      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6 text-sm text-amber-900">
        <div className="font-semibold mb-1">Framework stub</div>
        <div className="text-xs text-amber-800">
          Email delivery is simulated — no real SMTP is hit. ~2% of sends simulate bounces.
          Replace <code className="font-mono bg-amber-100 px-1 rounded">campaignService.dispatchEmail</code> with SendGrid/SES/Mailgun for production.
          Use the "Event simulator" on the detail panel to test open/click stats.
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Total campaigns" value={kpis.total} />
        <KpiTile label="Scheduled" value={kpis.scheduled}
          accent={kpis.scheduled > 0 ? 'amber' : 'default'} />
        <KpiTile label="Sent" value={kpis.sent} accent="sage" />
        <KpiTile label="Total recipients" value={kpis.totalRecipients} />
      </div>

      {/* Status filter chips */}
      <div className="card p-1.5 mb-4 flex gap-1 items-center overflow-x-auto">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatusFilter(s.value)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-pill transition-all whitespace-nowrap ${
              statusFilter === s.value
                ? 'bg-canvas-subtle text-ink-900'
                : 'text-ink-600 hover:bg-canvas-subtle'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {q.isLoading ? <div className="card"><LoadingState rows={5} /></div> :
       q.error ? <ErrorState error={q.error} /> : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_28rem] gap-6">
          <div className="card overflow-hidden">
            {!q.data?.length ? (
              <EmptyState
                message="No campaigns yet"
                hint="Create your first campaign to reach your customers."
                action={
                  <button className="btn-primary" onClick={() => setShowCreate(true)}>
                    + New campaign
                  </button>
                }
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Campaign</th>
                      <th>Audience</th>
                      <th className="text-right">Recipients</th>
                      <th>Status</th>
                      <th className="text-right">Open</th>
                      <th className="text-right">Click</th>
                      <th>Sent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(q.data ?? []).map((c: any) => (
                      <tr key={c.id} onClick={() => setSelectedId(c.id)}
                        className={`cursor-pointer ${selectedId === c.id ? '!bg-canvas-subtle' : ''}`}>
                        <td>
                          <div className="font-semibold text-ink-900">{c.name}</div>
                          <div className="text-xs text-ink-500 truncate max-w-xs mt-0.5">{c.subject}</div>
                        </td>
                        <td className="text-sm text-ink-700 capitalize">{c.audience_type}</td>
                        <td className="text-right font-mono text-sm tabular-nums">{c.recipient_count}</td>
                        <td><CampaignStatusBadge status={c.status} /></td>
                        <td className="text-right font-mono text-sm text-ink-700 tabular-nums">
                          {c.sent_count > 0 ? `${c.open_rate}%` : <span className="text-ink-300">—</span>}
                        </td>
                        <td className="text-right font-mono text-sm text-ink-700 tabular-nums">
                          {c.sent_count > 0 ? `${c.click_rate}%` : <span className="text-ink-300">—</span>}
                        </td>
                        <td className="text-xs text-ink-500">
                          {c.sent_at ? new Date(c.sent_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : <span className="text-ink-300">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card p-5 min-h-[400px]">
            {!selectedId ? (
              <EmptyState
                message="Select a campaign"
                hint="Click a row on the left to view stats, send, or simulate events."
              />
            ) : <CampaignDetail campaignId={selectedId}
                  onChanged={() => qc.invalidateQueries({ queryKey: ['campaigns'] })}
                  onDeleted={() => { setSelectedId(null); qc.invalidateQueries({ queryKey: ['campaigns'] }); }} />}
          </div>
        </div>
      )}

      {showCreate && <CreateCampaignModal
        onClose={() => setShowCreate(false)}
        onCreated={(id) => { qc.invalidateQueries({ queryKey: ['campaigns'] }); setShowCreate(false); setSelectedId(id); }} />}
    </div>
  );
}

function CampaignStatusBadge({ status }: { status: string }) {
  const cls = {
    draft: 'bg-canvas-subtle text-ink-700 ring-1 ring-inset ring-ink-200',
    scheduled: 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200',
    sending: 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200',
    sent: 'bg-sage-50 text-sage-700 ring-1 ring-inset ring-sage-200',
    cancelled: 'bg-canvas-subtle text-ink-500 ring-1 ring-inset ring-ink-200',
    failed: 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-200',
  }[status] ?? 'bg-canvas-subtle text-ink-700 ring-1 ring-inset ring-ink-200';
  return <span className={`badge ${cls} text-2xs`}>{status}</span>;
}

function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

function CampaignDetail({ campaignId, onChanged, onDeleted }:
    { campaignId: string; onChanged: () => void; onDeleted: () => void }) {
  const qc = useQueryClient();
  const [showSend, setShowSend] = useState(false);
  const [showEvent, setShowEvent] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const cQ = useQuery({
    queryKey: ['campaign', campaignId],
    queryFn: () => ecommerceApi.getCampaign(campaignId),
  });

  const statsQ = useQuery({
    queryKey: ['campaign-stats', campaignId],
    queryFn: () => ecommerceApi.getCampaignStats(campaignId),
    enabled: !!cQ.data && ['sending','sent','failed'].includes(cQ.data.status),
  });

  const deleteMut = useMutation({
    mutationFn: () => ecommerceApi.deleteCampaign(campaignId),
    onSuccess: () => { toast.success('Deleted'); onDeleted(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (cQ.isLoading) return <LoadingState />;
  if (cQ.error) return <ErrorState error={cQ.error} />;
  const c = cQ.data;
  const stats = statsQ.data;

  return (
    <div className="card p-4">
      <div className="flex justify-between items-start mb-3 gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-base text-ink-900 truncate">{c.name}</div>
          <div className="text-xs text-ink-500 truncate mt-0.5">{c.subject}</div>
          <div className="text-2xs text-ink-400 mt-1 font-mono">
            {c.audience_type.replace(/_/g,' ')} · {new Date(c.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
        </div>
        <CampaignStatusBadge status={c.status} />
      </div>

      {c.last_error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-xs text-red-700 mb-3">
          {c.last_error}
        </div>
      )}

      {stats && (
        <div className="border-t border-ink-100 pt-3 mb-3">
          <div className="eyebrow text-ink-500 mb-2">Stats</div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <StatCard label="Recipients" value={stats.counts.recipients} />
            <StatCard label="Sent"       value={stats.counts.sent} />
            <StatCard label="Failed"     value={stats.counts.failed}  tone={stats.counts.failed > 0 ? 'red' : 'neutral'} />
            <StatCard label="Opens"      value={stats.counts.opens} />
            <StatCard label="Clicks"     value={stats.counts.clicks} />
            <StatCard label="Bounces"    value={stats.counts.bounces} tone={stats.counts.bounces > 0 ? 'amber' : 'neutral'} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <StatCard label="Open rate"       value={stats.rates.open_rate}         isRate />
            <StatCard label="Click rate"      value={stats.rates.click_rate}        isRate />
            <StatCard label="Click-to-open"   value={stats.rates.click_to_open_rate} isRate />
            <StatCard label="Delivery rate"   value={stats.rates.delivery_rate}     isRate />
          </div>
          {stats.topClickedUrls?.length > 0 && (
            <div className="mt-3">
              <div className="eyebrow text-ink-500 mb-1.5">Top clicked URLs</div>
              {stats.topClickedUrls.map((u: any, i: number) => (
                <div key={i} className="text-2xs flex justify-between py-0.5">
                  <span className="font-mono truncate flex-1 mr-2 text-ink-700">{u.url}</span>
                  <span className="font-mono font-semibold tabular-nums text-ink-900">{u.clicks}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2 border-t border-ink-100 pt-3 flex-wrap">
        {['draft','scheduled'].includes(c.status) && (
          <button className="btn-primary text-xs flex-1" onClick={() => setShowSend(true)}>
            Send campaign
          </button>
        )}
        {c.status === 'sent' && (
          <button className="btn-secondary text-xs flex-1" onClick={() => setShowEvent(true)}>
            Simulate event
          </button>
        )}
        {['draft','cancelled','failed'].includes(c.status) && (
          <button
            className="text-xs font-semibold text-danger hover:bg-red-50 px-2 py-1 rounded transition-colors"
            onClick={() => setShowDelete(true)}
          >
            Delete
          </button>
        )}
      </div>

      {showSend && <SendCampaignModal campaign={c}
        onClose={() => setShowSend(false)}
        onSent={() => {
          setShowSend(false);
          qc.invalidateQueries({ queryKey: ['campaign', campaignId] });
          qc.invalidateQueries({ queryKey: ['campaign-stats', campaignId] });
          onChanged();
        }} />}

      {showEvent && <SimulateEventModal campaignId={campaignId}
        onClose={() => setShowEvent(false)}
        onRecorded={() => {
          qc.invalidateQueries({ queryKey: ['campaign', campaignId] });
          qc.invalidateQueries({ queryKey: ['campaign-stats', campaignId] });
        }} />}

      {showDelete && (
        <Modal
          onClose={() => setShowDelete(false)}
          overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
          <div className="px-6 pt-6 pb-2">
            <div className="eyebrow text-danger mb-1">Commerce · Delete</div>
            <h2 className="text-xl font-bold font-display text-ink-900">Delete campaign?</h2>
            <p className="text-sm text-ink-500 mt-2 leading-relaxed">
              Delete <span className="font-semibold text-ink-900">{c.name}</span>?
              The campaign and all recipient records will be permanently removed.
            </p>
          </div>
          <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
            <button className="btn-secondary" onClick={() => setShowDelete(false)}>Cancel</button>
            <button
              className="btn-primary !bg-danger hover:!bg-red-700"
              disabled={deleteMut.isPending}
              onClick={() => deleteMut.mutate()}
            >
              {deleteMut.isPending ? 'Deleting…' : 'Delete campaign'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function StatCard({ label, value, tone = 'neutral', isRate = false }:
    { label: string; value: number; tone?: 'neutral'|'red'|'amber'; isRate?: boolean }) {
  const vCls = tone === 'red' ? 'text-danger' : tone === 'amber' ? 'text-amber-700' : 'text-ink-900';
  return (
    <div className="bg-canvas-subtle rounded-xl p-2.5">
      <div className="text-2xs font-semibold uppercase tracking-widest text-ink-400 mb-1">{label}</div>
      <div className={`font-mono font-bold text-sm tabular-nums ${vCls}`}>
        {isRate ? `${value}%` : value.toLocaleString()}
      </div>
    </div>
  );
}

function CreateCampaignModal({ onClose, onCreated }:
    { onClose: () => void; onCreated: (id: string) => void }) {
  const sfQ = useQuery({ queryKey: ['storefronts'], queryFn: () => ecommerceApi.listStorefronts() });
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [fromName, setFromName] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [bodyHtml, setBodyHtml] = useState('<p>Hi {{name}},</p>\n\n<p>Thanks for shopping with us.</p>');
  const [storefrontId, setStorefrontId] = useState('');
  const [audienceType, setAudienceType] = useState<'all_customers'|'abandoned_cart'|'specific_customers'>('all_customers');
  const [abandonedDays, setAbandonedDays] = useState(7);

  const mut = useMutation({
    mutationFn: () => ecommerceApi.createCampaign({
      storefrontId: storefrontId || undefined,
      name, subject,
      fromName: fromName || undefined,
      fromEmail: fromEmail || undefined,
      previewText: previewText || undefined,
      bodyHtml,
      audienceType,
      audienceConfig: audienceType === 'abandoned_cart' ? { within_days: abandonedDays } : undefined,
    }),
    onSuccess: (d) => { toast.success('Campaign created'); onCreated(d.id); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-2 flex-shrink-0">
        <div className="eyebrow mb-1">Commerce · Campaign</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New campaign</h2>
        <p className="text-sm text-ink-500 mt-1">Creates a draft. Review before sending.</p>
      </div>

      <div className="px-6 py-5 grid grid-cols-2 gap-4 overflow-y-auto flex-1 min-h-0">
        <div className="col-span-2">
          <label className="label">Campaign name <span className="text-danger">*</span></label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Summer sale announcement" />
        </div>
        <div className="col-span-2">
          <label className="label">Subject line <span className="text-danger">*</span></label>
          <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)}
            placeholder="20% off this weekend only" />
        </div>
        <div>
          <label className="label">From name <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input" value={fromName} onChange={(e) => setFromName(e.target.value)}
            placeholder="OmniRetail" />
        </div>
        <div>
          <label className="label">From email <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input type="email" className="input" value={fromEmail}
            onChange={(e) => setFromEmail(e.target.value)} placeholder="hello@example.com" />
        </div>
        <div className="col-span-2">
          <label className="label">Preview text <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input" value={previewText} onChange={(e) => setPreviewText(e.target.value)}
            placeholder="Shown after subject in inbox preview" />
        </div>
        <div>
          <label className="label">Storefront <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <select className="input" value={storefrontId} onChange={(e) => setStorefrontId(e.target.value)}>
            <option value="">—</option>
            {sfQ.data?.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Audience</label>
          <select className="input" value={audienceType}
            onChange={(e) => setAudienceType(e.target.value as any)}>
            <option value="all_customers">All customers</option>
            <option value="abandoned_cart">Abandoned carts</option>
            <option value="specific_customers">Specific customers</option>
          </select>
        </div>
        {audienceType === 'abandoned_cart' && (
          <div className="col-span-2">
            <label className="label">Abandoned within last (days)</label>
            <input type="number" min={1} max={90} className="input tabular-nums" value={abandonedDays}
              onChange={(e) => setAbandonedDays(parseInt(e.target.value) || 7)} />
          </div>
        )}
        {audienceType === 'specific_customers' && (
          <div className="col-span-2 text-2xs text-ink-500 leading-relaxed">
            Specific-customer audience requires customer IDs — set these via API after creation.
          </div>
        )}
        <div className="col-span-2">
          <label className="label">Body HTML <span className="text-danger">*</span></label>
          <textarea className="input font-mono text-xs" rows={10} value={bodyHtml}
            onChange={(e) => setBodyHtml(e.target.value)} />
          <div className="text-2xs text-ink-500 mt-1 leading-relaxed">
            Template merges (&#123;&#123;name&#125;&#125; etc.) won't be substituted without a real dispatcher connected.
          </div>
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end flex-shrink-0">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!name || !subject || !bodyHtml || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create draft'}
        </button>
      </div>
    </Modal>
  );
}

function SendCampaignModal({ campaign, onClose, onSent }:
    { campaign: any; onClose: () => void; onSent: () => void }) {
  const [testOnly, setTestOnly] = useState(true);
  const [testEmails, setTestEmails] = useState('');

  const mut = useMutation({
    mutationFn: () => {
      const emails = testEmails.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
      return ecommerceApi.sendCampaign(campaign.id,
        testOnly ? { testOnly: true, testEmails: emails } : { testOnly: false });
    },
    onSuccess: (d: any) => {
      toast.success(
        d.testOnly
          ? `Test send: ${d.sent} ok, ${d.failed} failed (simulated)`
          : `Campaign sent to ${d.audienceResolved} recipients: ${d.sent} ok, ${d.failed} failed`,
        { duration: 7000 }
      );
      onSent();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Commerce · Send</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Send campaign</h2>
        <div className="mt-3 bg-canvas-subtle rounded-xl px-4 py-3 text-sm space-y-0.5">
          <div className="font-semibold text-ink-900">{campaign.name}</div>
          <div className="text-ink-500 text-xs">{campaign.subject}</div>
          <div className="text-ink-400 text-2xs font-mono">
            Audience: {campaign.audience_type.replace(/_/g,' ')}
          </div>
        </div>
      </div>

      <div className="px-6 py-5 space-y-0">
        <label className="flex items-start gap-2.5 py-3 border-b border-ink-100 cursor-pointer">
          <input type="radio" checked={testOnly} onChange={() => setTestOnly(true)} className="mt-0.5" />
          <div>
            <div className="text-sm font-medium text-ink-900">Test send</div>
            <div className="text-2xs text-ink-500 mt-0.5 leading-relaxed">
              Delivers to the addresses below only. No recipients are recorded in campaign stats.
            </div>
          </div>
        </label>
        <label className="flex items-start gap-2.5 py-3 cursor-pointer">
          <input type="radio" checked={!testOnly} onChange={() => setTestOnly(false)} className="mt-0.5" />
          <div>
            <div className="text-sm font-medium text-ink-900">Real send</div>
            <div className="text-2xs text-ink-500 mt-0.5 leading-relaxed">
              Expands to the full audience and marks the campaign as sent.
              <span className="text-danger font-semibold"> Irreversible.</span>
            </div>
          </div>
        </label>
      </div>

      {testOnly && (
        <div className="px-6 pb-2">
          <label className="label">Test addresses <span className="font-normal text-ink-400 normal-case tracking-normal">(comma or space separated)</span></label>
          <textarea className="input text-xs" rows={2} value={testEmails}
            onChange={(e) => setTestEmails(e.target.value)}
            placeholder="admin@example.com, test@example.com" />
        </div>
      )}

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className={`btn-primary ${!testOnly ? '!bg-danger hover:!bg-red-700' : ''}`}
          disabled={mut.isPending || (testOnly && !testEmails.trim())}
          onClick={() => mut.mutate()}
        >
          {mut.isPending ? 'Sending…' : (testOnly ? 'Send test' : 'Send now')}
        </button>
      </div>
    </Modal>
  );
}

function SimulateEventModal({ campaignId, onClose, onRecorded }:
    { campaignId: string; onClose: () => void; onRecorded: () => void }) {
  const [email, setEmail] = useState('');
  const [eventType, setEventType] = useState<'open'|'click'|'bounce'|'unsubscribe'|'complaint'>('open');
  const [url, setUrl] = useState('');

  const mut = useMutation({
    mutationFn: () => ecommerceApi.recordCampaignEvent(campaignId, {
      email, eventType, url: url || undefined,
    }),
    onSuccess: () => { toast.success(`${eventType} recorded`); onRecorded(); onClose(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Commerce · Simulate</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Simulate event</h2>
        <p className="text-sm text-ink-500 mt-1 leading-relaxed">
          In production the email provider posts events to a webhook. For demo, record them
          manually here to exercise the stats pipeline.
        </p>
      </div>

      <div className="px-6 py-5 space-y-4">
        <div>
          <label className="label">Recipient email <span className="text-danger">*</span></label>
          <input type="email" className="input" value={email}
            onChange={(e) => setEmail(e.target.value)} placeholder="customer@example.com" />
          <div className="text-2xs text-ink-500 mt-1">
            Must match a recipient address on this campaign.
          </div>
        </div>
        <div>
          <label className="label">Event type</label>
          <select className="input" value={eventType}
            onChange={(e) => setEventType(e.target.value as any)}>
            <option value="open">Open</option>
            <option value="click">Click</option>
            <option value="bounce">Bounce</option>
            <option value="unsubscribe">Unsubscribe</option>
            <option value="complaint">Spam complaint</option>
          </select>
        </div>
        {eventType === 'click' && (
          <div>
            <label className="label">Clicked URL</label>
            <input className="input font-mono text-xs" value={url} onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/sale" />
          </div>
        )}
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!email || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Recording…' : 'Record event'}
        </button>
      </div>
    </Modal>
  );
}
